import {
  ExecutionStep,
  ExecutionStepResult,
  PipelineExecutionState,
  ReasoningTraceEvent,
} from '@/interfaces/pipelineExecution.interface';
import { EntityType } from '@/components/entities/entityType';
import { useApiContext } from '@/contexts/apiContext';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Collapse,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import AppsIcon from '@mui/icons-material/Apps';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SyncIcon from '@mui/icons-material/Sync';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PsychologyIcon from '@mui/icons-material/Psychology';
import HandymanIcon from '@mui/icons-material/Handyman';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HubIcon from '@mui/icons-material/Hub';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import TerminalIcon from '@mui/icons-material/Terminal';
import CodeIcon from '@mui/icons-material/Code';
import { formatDate } from 'date-fns';
import { formatRunDuration } from '@/utils';
import ErrorMessage from '@/components/errorMessage';
import StepItemsView from './stepItemsView';

const getEntityInstanceUrl = (entityType?: EntityType, entityDefId?: string, entityId?: string): string | null => {
  if (!entityType || !entityId) return null;
  const defId = entityDefId || entityId;
  switch (entityType) {
    case EntityType.dataSourceInstance:
      return `/dataSourceInstance/${defId}/${entityId}`;
    case EntityType.agentInstance:
      return `/agentInstance/${defId}/${entityId}`;
    case EntityType.userAppInstance:
      return `/userAppInstance/${defId}/${entityId}`;
    default:
      return null;
  }
};

type Props = {
  step: ExecutionStep;
  pipelineExecutionState?: PipelineExecutionState;
  suppressEvents?: boolean;
  onToggleSuppressEvents?: (value: boolean) => void;
  onExecuteStep?: (
    step: ExecutionStep,
    options?: { inputOverride?: any; reingest?: boolean; suppressEvents?: boolean },
  ) => Promise<void> | void;
  onRestartFromPoint: (step: ExecutionStep, eventDataId?: string | number) => void;
  isExecuting?: boolean;
  stepExecutionResponse?: ExecutionStepResult | null;
  onClearStepExecutionResponse?: () => void;
  onChange?: () => void;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`step-tabpanel-${index}`}
      aria-labelledby={`step-tab-${index}`}
      sx={{
        flexGrow: 1,
        height: '100%',
        minHeight: 0,
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 1, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `step-tab-${index}`,
    'aria-controls': `step-tabpanel-${index}`,
  };
}

const StepViewer: React.FC<Props> = ({
  step,
  pipelineExecutionState,
  suppressEvents,
  onToggleSuppressEvents,
  onExecuteStep,
  onRestartFromPoint,
  isExecuting = false,
  stepExecutionResponse,
  onClearStepExecutionResponse,
  onChange,
}) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const [tabIndex, setTabIndex] = useState(0);
  const [defName, setDefName] = useState<string>(step?.entityDefName || '');
  const [usageStats, setUsageStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [configuredSourceEntities, setConfiguredSourceEntities] = useState<any[]>([]);
  const [copiedEntityId, setCopiedEntityId] = useState(false);
  const [localSuppressEvents, setLocalSuppressEvents] = useState<boolean>(false);
  const [dismissedStepError, setDismissedStepError] = useState<boolean>(false);
  const [approvalFeedback, setApprovalFeedback] = useState<string>('');
  const [isEditingParams, setIsEditingParams] = useState<boolean>(false);
  const [editedParams, setEditedParams] = useState<string>('');
  const [isSubmittingApproval, setIsSubmittingApproval] = useState<boolean>(false);

  useEffect(() => {
    setDismissedStepError(false);
  }, [step?.id, isExecuting]);

  useEffect(() => {
    if (step.approvalRequest?.parameters || step.approvalRequest?.proposedAction?.parameters) {
      const params = step.approvalRequest.parameters || step.approvalRequest.proposedAction?.parameters;
      setEditedParams(JSON.stringify(params, null, 2));
    }
  }, [step.approvalRequest]);

  const activeSuppressEvents = suppressEvents !== undefined ? suppressEvents : localSuppressEvents;
  const handleToggleSuppressEvents = onToggleSuppressEvents || setLocalSuppressEvents;

  const handleApproveAction = async () => {
    setIsSubmittingApproval(true);
    let parsedModifiedArgs: any = undefined;
    if (isEditingParams && editedParams.trim()) {
      try {
        parsedModifiedArgs = JSON.parse(editedParams);
      } catch (e) {
        console.warn('Could not parse edited parameters as JSON:', e);
      }
    }

    try {
      const pipelineInstanceId = pipelineExecutionState?.pipelineInstanceId;
      const entityId = step.entityId || step.id;
      if (pipelineInstanceId && entityId) {
        try {
          await apiContext.resumePipelineStep(pipelineInstanceId, entityId, {
            approved: true,
            feedback: approvalFeedback,
            modifiedArgs: parsedModifiedArgs,
            entityDefId: step.entityDefId,
          });
          onChange?.();
          return;
        } catch (apiErr) {
          console.warn('Direct resumePipelineStep failed, falling back to onExecuteStep:', apiErr);
        }
      }

      if (onExecuteStep) {
        await onExecuteStep(step, {
          inputOverride: {
            action: 'resume',
            approved: true,
            feedback: approvalFeedback,
            modifiedArgs: parsedModifiedArgs,
          },
          suppressEvents: activeSuppressEvents,
        });
      }
    } catch (err) {
      console.error('Failed to approve step action:', err);
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  const handleRejectApproval = async () => {
    setIsSubmittingApproval(true);
    try {
      const pipelineInstanceId = pipelineExecutionState?.pipelineInstanceId;
      const entityId = step.entityId || step.id;
      if (pipelineInstanceId && entityId) {
        try {
          await apiContext.resumePipelineStep(pipelineInstanceId, entityId, {
            approved: false,
            feedback: approvalFeedback,
            entityDefId: step.entityDefId,
          });
          onChange?.();
          return;
        } catch (apiErr) {
          console.warn('Direct resumePipelineStep failed, falling back to onExecuteStep:', apiErr);
        }
      }

      if (onExecuteStep) {
        await onExecuteStep(step, {
          inputOverride: {
            action: 'reject',
            approved: false,
            feedback: approvalFeedback,
          },
          suppressEvents: activeSuppressEvents,
        });
      }
    } catch (err) {
      console.error('Failed to reject step action:', err);
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  const traceEvents: ReasoningTraceEvent[] = useMemo(() => {
    // 1. If explicit reasoningTrace is provided, use it directly
    if (step.reasoningTrace && step.reasoningTrace.length > 0) {
      return step.reasoningTrace;
    }

    // 2. Extract trajectory from step.checkpointState, step.lastRun?.output, step.output, or step.latestRun
    const rawTrajectory: any[] =
      (step.checkpointState && Array.isArray(step.checkpointState.trajectory) ? step.checkpointState.trajectory : null) ||
      (step.output && Array.isArray(step.output.trajectory) ? step.output.trajectory : null) ||
      (step.lastRun?.output && Array.isArray(step.lastRun.output.trajectory) ? step.lastRun.output.trajectory : null) ||
      (step.latestRun?.output && Array.isArray(step.latestRun.output.trajectory) ? step.latestRun.output.trajectory : null) ||
      (step.output && Array.isArray(step.output.trace) ? step.output.trace : null) ||
      (step.lastRun?.output && Array.isArray(step.lastRun.output.trace) ? step.lastRun.output.trace : null);

    if (rawTrajectory && rawTrajectory.length > 0) {
      const mappedEvents: ReasoningTraceEvent[] = [];
      let currentNumber = 1;

      for (const item of rawTrajectory) {
        if (!item || typeof item !== 'object') continue;

        if (item.thought) {
          mappedEvents.push({
            id: `thought-${item.iteration ?? currentNumber}`,
            stepNumber: currentNumber++,
            type: 'thought',
            timestamp: item.timestamp || '',
            thought: item.thought,
          });
        }

        if (item.tool_name || item.toolName || item.action) {
          const tool = item.tool_name || item.toolName || item.action;
          if (tool === 'final_answer') {
            mappedEvents.push({
              id: `final-${item.iteration ?? currentNumber}`,
              stepNumber: currentNumber++,
              type: 'final_answer',
              timestamp: item.timestamp || '',
              thought: item.thought || (typeof item.tool_args === 'string' ? item.tool_args : JSON.stringify(item.tool_args || item.output || '')),
            });
          } else if (tool === 'require_approval') {
            mappedEvents.push({
              id: `approval-${item.iteration ?? currentNumber}`,
              stepNumber: currentNumber++,
              type: 'approval_required',
              timestamp: item.timestamp || '',
              thought: item.thought || 'Execution paused for human approval.',
            });
          } else {
            mappedEvents.push({
              id: `tool-${item.iteration ?? currentNumber}`,
              stepNumber: currentNumber++,
              type: 'tool_call',
              timestamp: item.timestamp || '',
              toolName: tool,
              toolInput: item.tool_args || item.toolInput || item.parameters,
              durationMs: item.duration_ms || item.durationMs,
            });
          }
        }

        if (item.observation || item.toolOutput || item.result) {
          mappedEvents.push({
            id: `obs-${item.iteration ?? currentNumber}`,
            stepNumber: currentNumber++,
            type: 'observation',
            timestamp: item.timestamp || '',
            toolOutput: typeof item.observation === 'object' ? item.observation : { result: item.observation || item.toolOutput || item.result },
          });
        }
      }

      if (mappedEvents.length > 0) {
        return mappedEvents;
      }
    }

    // 3. If step is awaiting approval and has an approvalRequest, synthesize an approval event
    if (step.status === 'awaiting_approval' && step.approvalRequest) {
      return [
        {
          id: 'approval-required-1',
          stepNumber: 1,
          type: 'approval_required',
          timestamp: step.approvalRequest.requestedAt
            ? new Date(step.approvalRequest.requestedAt).toLocaleTimeString()
            : '',
          thought:
            step.approvalRequest.prompt ||
            (step.approvalRequest.action
              ? `Action '${step.approvalRequest.action}' requires human authorization.`
              : 'Execution paused for human authorization before executing action.'),
        },
      ];
    }

    return [];
  }, [step.reasoningTrace, step.checkpointState, step.output, step.lastRun, step.latestRun, step.status, step.approvalRequest]);

  const instanceUrl = useMemo(() => {
    return getEntityInstanceUrl(step.entityType, step.entityDefId, step.entityId);
  }, [step.entityType, step.entityDefId, step.entityId]);

  const handleCopyEntityId = () => {
    if (step.entityId) {
      navigator.clipboard.writeText(step.entityId);
      setCopiedEntityId(true);
      setTimeout(() => setCopiedEntityId(false), 2000);
    }
  };

  useEffect(() => {
    if (step?.entityDefName) {
      setDefName(step.entityDefName);
      return;
    }
    if (!step?.entityDefId || !step?.entityType) {
      setDefName('');
      return;
    }

    let isMounted = true;
    const fetchDef = async () => {
      try {
        let def: any = null;
        if (step.entityType === EntityType.dataSourceInstance) {
          def = await apiContext.getDataSourceDef(step.entityDefId);
        } else if (step.entityType === EntityType.agentInstance) {
          def = await apiContext.getAgentDef(step.entityDefId);
        } else if (step.entityType === EntityType.userAppInstance) {
          def = await apiContext.getUserAppDef(step.entityDefId);
        }
        if (isMounted && def?.name) {
          setDefName(def.name);
        }
      } catch (e) {
        // fallback
      }
    };

    fetchDef();
    return () => {
      isMounted = false;
    };
  }, [step?.entityDefId, step?.entityType, step?.entityDefName]);

  // Fetch usage stats and configured source entities when Details & Metrics tab is opened
  useEffect(() => {
    if (tabIndex !== 2 || !step?.entityDefId || !step?.entityId) return;

    let isMounted = true;
    const fetchStatsAndSources = async () => {
      setLoadingStats(true);
      try {
        if (step.entityType === EntityType.userAppInstance) {
          const [stats, sources] = await Promise.allSettled([
            apiContext.getUserAppInstanceUsageStats(step.entityDefId, step.entityId),
            apiContext.getUserAppInstanceSourceEntities(step.entityDefId, step.entityId),
          ]);
          if (isMounted) {
            if (stats.status === 'fulfilled') setUsageStats(stats.value);
            if (sources.status === 'fulfilled') setConfiguredSourceEntities(sources.value || []);
          }
        } else if (step.entityType === EntityType.agentInstance) {
          const stats = await apiContext.getAgentInstanceUsageStats(step.entityDefId, step.entityId);
          if (isMounted) setUsageStats(stats);
        } else if (step.entityType === EntityType.dataSourceInstance) {
          const stats = await apiContext.getDataSourceInstanceUsageStats(step.entityDefId, step.entityId);
          if (isMounted) setUsageStats(stats);
        }
      } catch (err) {
        console.error('Error fetching step metrics:', err);
      } finally {
        if (isMounted) setLoadingStats(false);
      }
    };

    fetchStatsAndSources();
    return () => {
      isMounted = false;
    };
  }, [tabIndex, step?.entityDefId, step?.entityId, step?.entityType]);

  const stepIndex = pipelineExecutionState?.steps.findIndex((s) => s.id === step.id);

  const isResponseForCurrentStep = useMemo(() => {
    if (!stepExecutionResponse) return false;
    if (!stepExecutionResponse.stepId) return true;
    const respStepId = String(stepExecutionResponse.stepId).toLowerCase().trim();
    const currentStepId = String(step.id || '')
      .toLowerCase()
      .trim();
    const currentEntityId = String(step.entityId || '')
      .toLowerCase()
      .trim();
    const currentEntityDefId = String(step.entityDefId || '')
      .toLowerCase()
      .trim();

    if (respStepId === currentStepId || respStepId === currentEntityId || respStepId === currentEntityDefId) {
      return true;
    }

    if (stepIndex !== undefined && stepIndex !== -1) {
      const idxNum = String(stepIndex + 1);
      if (respStepId === idxNum || respStepId === `step_${idxNum}` || respStepId === `step${idxNum}`) {
        return true;
      }
    }
    return false;
  }, [stepExecutionResponse, step, stepIndex]);

  // Compute Source Agents related to this step
  const sourceAgents = useMemo(() => {
    if (step.entityType === EntityType.userAppInstance) {
      // 1. Pipeline agents
      const pipelineAgents =
        pipelineExecutionState?.steps?.filter((s) => s.entityType === EntityType.agentInstance) || [];

      // Combine pipeline agents with configured source entities (deduplicating by entityId)
      const agentMap = new Map<string, { id: string; name: string; entityDefName?: string; inPipeline: boolean }>();

      for (const ag of pipelineAgents) {
        agentMap.set(ag.entityId, {
          id: ag.entityId,
          name: ag.entityName || ag.name || 'Agent',
          entityDefName: ag.entityDefName,
          inPipeline: true,
        });
      }

      for (const se of configuredSourceEntities) {
        if (se.entityType === EntityType.agentInstance || se.sourceEntityType === EntityType.agentInstance) {
          const id = se.id || se.sourceEntityId;
          const name = se.name || se.sourceEntityName || 'Agent';
          if (id && !agentMap.has(id)) {
            agentMap.set(id, {
              id,
              name,
              entityDefName: se.agentDef?.name || se.entityDefName,
              inPipeline: false,
            });
          }
        }
      }

      return Array.from(agentMap.values());
    }

    if (step.entityType === EntityType.agentInstance) {
      // Upstream agents in the pipeline
      const upstreamAgents =
        pipelineExecutionState?.steps?.filter(
          (s) =>
            s.entityType === EntityType.agentInstance &&
            step.upstreamEntities?.some((ue) => ue.entityId === s.entityId),
        ) || [];

      return upstreamAgents.map((ag) => ({
        id: ag.entityId,
        name: ag.entityName || ag.name || 'Upstream Agent',
        entityDefName: ag.entityDefName,
        inPipeline: true,
      }));
    }

    return [];
  }, [step, pipelineExecutionState, configuredSourceEntities]);

  // Compute Data Sources connected in this pipeline
  const connectedDataSources = useMemo(() => {
    return pipelineExecutionState?.steps?.filter((s) => s.entityType === EntityType.dataSourceInstance) || [];
  }, [pipelineExecutionState]);

  const renderMetricValue = (val: any): React.ReactNode => {
    if (val === undefined || val === null) return '-';
    if (typeof val === 'number' || typeof val === 'string' || typeof val === 'boolean') {
      return String(val);
    }
    if (typeof val === 'object') {
      if (val.interactions !== undefined) {
        return (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
              {val.interactions}
            </Typography>
            {(val.positiveFeedback !== undefined || val.negativeFeedback !== undefined) && (
              <Typography variant="caption" color="text.secondary">
                (👍 {val.positiveFeedback ?? 0} • 👎 {val.negativeFeedback ?? 0})
              </Typography>
            )}
          </Stack>
        );
      }
      if (val.total !== undefined || val.count !== undefined) {
        return String(val.total ?? val.count);
      }
      return JSON.stringify(val);
    }
    return String(val);
  };

  if (!step) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <Typography color="text.secondary">No item selected</Typography>
      </Paper>
    );
  }

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'completed':
      case 'active':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'running':
      case 'processing':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getTabLabel = (index: number) => {
    if (index === 0) return `Items`;
    if (index === 1) return 'Reasoning Trace';
    return 'Details & Metrics';
  };

  const entityTypeLabel = step.entityType ? t(`common.entityType.${step.entityType}`, step.entityType) : '';

  return (
    <Paper
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, flexGrow: 1, overflow: 'hidden' }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 0.5, borderBottom: 1, borderColor: 'divider', bgcolor: '#ffffff', gap: 1, flexShrink: 0 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ minWidth: 0, flexShrink: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }} noWrap>
            {pipelineExecutionState && stepIndex !== undefined && stepIndex !== -1 ? `Step ${stepIndex + 1}: ` : ''}
            {step.entityName || defName || step.entityDefId || 'Item'}
            {entityTypeLabel ? ` (${entityTypeLabel})` : ''}
          </Typography>
          {isExecuting || (step.status as string) === 'running' || (step.status as string) === 'processing' ? (
            <Chip
              label="Running"
              color="primary"
              size="small"
              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, px: 0.5 }}
            />
          ) : (
            (() => {
              const duration = formatRunDuration(step.lastRun?.startTime, step.lastRun?.endTime);
              const successCount = step.lastRun?.successCount ?? 0;
              const failedCount = step.lastRun?.failedCount ?? 0;
              const totalItems = step.lastRun?.totalItems ?? 0;
              const itemCountText =
                step.lastRun?.totalItems !== undefined
                  ? totalItems === 0
                    ? '0 processed'
                    : failedCount > 0
                      ? `${successCount} ok, ${failedCount} fail`
                      : `${successCount} processed`
                  : null;

              const lastRunSummary = step.lastRun
                ? `${itemCountText ?? step.status}${duration ? ` • ${duration}` : ''}`
                : step.status || 'Pending';

              return (
                <Chip
                  label={lastRunSummary}
                  color={getStatusColor(step.status)}
                  variant={step.lastRun ? 'outlined' : 'filled'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}
                />
              );
            })()
          )}
          {step.executionMode === 'react' && (
            <Chip
              icon={<AutoModeIcon sx={{ fontSize: '13px !important' }} />}
              label="ReAct Loop"
              size="small"
              color="info"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
            />
          )}
          {step.executionMode === 'swarm_supervisor' && (
            <Chip
              icon={<HubIcon sx={{ fontSize: '13px !important' }} />}
              label={`Swarm (${step.subagentsCount ?? 3} agents)`}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
            />
          )}
          {step.status === 'awaiting_approval' && (
            <Chip
              icon={<GavelIcon sx={{ fontSize: '13px !important', color: '#b45309' }} />}
              label="Awaiting Approval"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.68rem',
                fontWeight: 700,
                bgcolor: '#fef3c7',
                color: '#b45309',
                border: '1px solid #f59e0b',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          )}
        </Stack>

        {/* Right side: 'Run this step only' toggle, Entity ID, Open & Refresh */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {/* Run this step only Checkbox */}
          <Tooltip
            title={
              activeSuppressEvents
                ? 'Run this step only: Executes this step without triggering subsequent steps in the pipeline.'
                : 'Run and continue: Executes this step and automatically triggers subsequent steps in the pipeline.'
            }
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={activeSuppressEvents}
                  onChange={(e) => handleToggleSuppressEvents(e.target.checked)}
                  disabled={isExecuting}
                  sx={{
                    p: 0.25,
                    color: '#94a3b8',
                    '&.Mui-checked': { color: '#2563eb' },
                    '& .MuiSvgIcon-root': { fontSize: 16 },
                  }}
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: activeSuppressEvents ? '#1d4ed8' : '#64748b',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Run this step only
                </Typography>
              }
              sx={{ mr: 0.25, ml: 0 }}
            />
          </Tooltip>

          {step.entityId && (
            <Tooltip title={copiedEntityId ? 'Copied!' : 'Click to copy Entity Instance ID'}>
              <Chip
                label={`ID: ${step.entityId}`}
                size="small"
                onClick={handleCopyEntityId}
                icon={
                  copiedEntityId ? (
                    <CheckIcon sx={{ fontSize: '12px !important', color: '#16a34a' }} />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: '11px !important', color: '#64748b' }} />
                  )
                }
                sx={{
                  height: 22,
                  fontSize: '0.68rem',
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  bgcolor: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  '&:hover': { bgcolor: '#e2e8f0' },
                }}
              />
            </Tooltip>
          )}
          {instanceUrl && (
            <Tooltip title="Open entity instance in a new window">
              <Button
                size="small"
                variant="outlined"
                component="a"
                href={instanceUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  py: 0,
                  px: 1,
                  textTransform: 'none',
                  borderColor: '#cbd5e1',
                  color: '#1e293b',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                Open
              </Button>
            </Tooltip>
          )}
          {onChange && (
            <Tooltip title="Refresh step details and items">
              <IconButton
                size="small"
                onClick={onChange}
                disabled={isExecuting}
                sx={{
                  height: 22,
                  width: 22,
                  p: 0,
                  border: '1px solid #cbd5e1',
                  borderRadius: 1,
                  bgcolor: '#ffffff',
                  color: '#1e293b',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                <SyncIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {isResponseForCurrentStep &&
        stepExecutionResponse &&
        (stepExecutionResponse.status === 'failed' || Boolean(stepExecutionResponse.error)) && (
          <Box sx={{ mx: 1, mt: 0.75, mb: 0.25 }}>
            <ErrorMessage
              error={{
                message: `Step Execution Failed: ${stepExecutionResponse.error || 'Execution encountered an error.'}`,
              }}
              onClose={onClearStepExecutionResponse}
            />
          </Box>
        )}
      {!isResponseForCurrentStep &&
        !isExecuting &&
        (step.status as string) !== 'running' &&
        (step.status as string) !== 'processing' &&
        !dismissedStepError &&
        (step.status === 'failed' || Boolean(step.error || step.lastRun?.error)) && (
          <Box sx={{ mx: 1, mt: 0.75, mb: 0.25 }}>
            <ErrorMessage
              error={{
                message: `Step Status: Failed. ${step.error || step.lastRun?.error || 'Step failed during execution. Please check the logs or retry.'}`,
              }}
              onClose={() => setDismissedStepError(true)}
            />
          </Box>
        )}

      {/* Human-in-the-Loop Approval Banner Card */}
      {(step.status === 'awaiting_approval' || step.approvalRequest) && (
        <Paper
          variant="outlined"
          sx={{
            m: 1,
            p: 1.5,
            bgcolor: '#fffbeb',
            borderColor: '#f59e0b',
            borderRadius: 1.5,
            boxShadow: '0 1px 3px 0 rgba(245, 158, 11, 0.15)',
          }}
        >
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <GavelIcon sx={{ color: '#d97706', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e', fontSize: '0.85rem' }}>
                  Human-in-the-Loop Authorization Required
                </Typography>
                <Chip
                  label="Suspended"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    bgcolor: '#fef3c7',
                    color: '#b45309',
                    fontWeight: 700,
                    border: '1px solid #fde68a',
                  }}
                />
              </Stack>
              <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 500 }}>
                {step.approvalRequest?.requestedAt
                  ? new Date(step.approvalRequest.requestedAt).toLocaleTimeString()
                  : 'Action pending review'}
              </Typography>
            </Stack>

            <Typography variant="body2" sx={{ color: '#78350f', fontSize: '0.8rem' }}>
              {step.approvalRequest?.prompt ||
                'The autonomous agent has generated an action or mutation requiring explicit human confirmation before executing in production.'}
            </Typography>

            {step.approvalRequest?.proposedAction && (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  bgcolor: '#ffffff',
                  borderRadius: 1,
                  border: '1px solid #fde68a',
                  fontSize: '0.72rem',
                  fontFamily: 'monospace',
                  maxHeight: 110,
                  overflow: 'auto',
                  color: '#1e293b',
                }}
              >
                {JSON.stringify(step.approvalRequest.proposedAction, null, 2)}
              </Box>
            )}

            <Collapse in={isEditingParams}>
              <Box sx={{ mt: 0.5, mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', display: 'block', mb: 0.5 }}>
                  Edit Tool Parameters (JSON):
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  size="small"
                  value={editedParams}
                  onChange={(e) => setEditedParams(e.target.value)}
                  fullWidth
                  disabled={isExecuting || isSubmittingApproval}
                  sx={{
                    bgcolor: '#ffffff',
                    '& .MuiInputBase-input': { fontSize: '0.72rem', fontFamily: 'monospace', py: 0.75, px: 1 },
                  }}
                />
              </Box>
            </Collapse>

            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Optional guidance or parameter override for agent..."
                value={approvalFeedback}
                onChange={(e) => setApprovalFeedback(e.target.value)}
                fullWidth
                disabled={isExecuting || isSubmittingApproval}
                sx={{
                  bgcolor: '#ffffff',
                  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.6, px: 1 },
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setIsEditingParams((prev) => !prev)}
                disabled={isExecuting || isSubmittingApproval}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  height: 28,
                  minWidth: 105,
                  flexShrink: 0,
                  color: '#b45309',
                  borderColor: '#f59e0b',
                  '&:hover': { borderColor: '#d97706', bgcolor: '#fef3c7' },
                }}
              >
                {isEditingParams ? 'Hide Params' : 'Edit Params'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={
                  isSubmittingApproval ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <CancelIcon sx={{ fontSize: 14 }} />
                  )
                }
                onClick={handleRejectApproval}
                disabled={isExecuting || isSubmittingApproval}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  height: 28,
                  minWidth: 90,
                  flexShrink: 0,
                }}
              >
                Reject
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={
                  isSubmittingApproval ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                  )
                }
                onClick={handleApproveAction}
                disabled={isExecuting || isSubmittingApproval}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  height: 28,
                  minWidth: 130,
                  flexShrink: 0,
                  bgcolor: '#16a34a',
                  '&:hover': { bgcolor: '#15803d' },
                }}
              >
                Approve & Resume
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc', flexShrink: 0 }}>
        <Tabs
          value={tabIndex}
          onChange={handleChangeTab}
          aria-label="details tabs"
          sx={{
            minHeight: 32,
            '& .MuiTab-root': { minHeight: 32, py: 0.25, px: 1.5, fontSize: '0.75rem', fontWeight: 600 },
          }}
        >
          <Tab label={getTabLabel(0)} {...a11yProps(0)} />
          <Tab
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <span>Reasoning Trace</span>
                {(step.executionMode === 'react' ||
                  step.executionMode === 'swarm_supervisor' ||
                  step.status === 'awaiting_approval') && (
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#2563eb' }} />
                )}
              </Stack>
            }
            {...a11yProps(1)}
          />
          <Tab label={getTabLabel(2)} {...a11yProps(2)} />
        </Tabs>
      </Box>

      <CustomTabPanel value={tabIndex} index={0}>
        <StepItemsView
          step={step}
          pipelineExecutionState={pipelineExecutionState}
          onExecuteStep={onExecuteStep}
          suppressEvents={activeSuppressEvents}
          onToggleSuppressEvents={handleToggleSuppressEvents}
          onRestartFromPoint={onRestartFromPoint}
          isExecuting={isExecuting}
          stepExecutionResponse={stepExecutionResponse}
          onClearStepExecutionResponse={onClearStepExecutionResponse}
          onChange={onChange}
        />
      </CustomTabPanel>

      <CustomTabPanel value={tabIndex} index={1}>
        <Box sx={{ overflowY: 'auto', flexGrow: 1, pr: 0.5 }}>
          <Stack spacing={1.5}>
            {/* Top Overview: Execution mode, sandbox status, OpenTelemetry trace */}
            <Paper variant="outlined" sx={{ p: 1.25, bgcolor: '#f8fafc', borderRadius: 1.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AutoModeIcon sx={{ color: '#2563eb', fontSize: 18 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e293b' }}>
                    {step.executionMode === 'swarm_supervisor'
                      ? 'Swarm Supervisor Orchestrator'
                      : step.executionMode === 'react'
                        ? 'Autonomous ReAct Engine (Loop v2.0)'
                        : 'Standard Pipeline Execution'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  {step.sandboxProvider && (
                    <Chip
                      icon={<TerminalIcon sx={{ fontSize: '13px !important' }} />}
                      label={`Sandbox: ${step.sandboxProvider}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 22,
                        fontSize: '0.68rem',
                        bgcolor: '#ecfdf5',
                        color: '#065f46',
                        borderColor: '#a7f3d0',
                      }}
                    />
                  )}
                  <Chip
                    label={`Trace: ${step.id ? `tr-${step.id.slice(0, 8)}` : 'otel-active'}`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      fontFamily: 'monospace',
                      bgcolor: '#e2e8f0',
                      color: '#334155',
                    }}
                  />
                </Stack>
              </Stack>
            </Paper>

            {/* If Swarm Supervisor: render Subagents Tree */}
            {step.subagents && step.subagents.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1.25, bgcolor: '#faf5ff', borderColor: '#e9d5ff', borderRadius: 1.5 }}>
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <HubIcon sx={{ color: '#7e22ce', fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#581c87', fontSize: '0.8rem' }}>
                        Active Subagents Swarm ({step.subagents.length} Agents)
                      </Typography>
                    </Stack>
                    <Chip
                      label={step.subagentsRunning ? `${step.subagentsRunning} active` : 'Parallel Execution'}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#f3e8ff', color: '#6b21a8', fontWeight: 600 }}
                    />
                  </Stack>
                  <Stack spacing={0.75}>
                    {step.subagents.map((agent, i) => (
                      <Box
                        key={i}
                        sx={{
                          p: 1,
                          bgcolor: '#ffffff',
                          borderRadius: 1,
                          border: '1px solid #e9d5ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SmartToyIcon sx={{ fontSize: 16, color: '#7e22ce' }} />
                          <Box>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Typography
                                variant="body2"
                                sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e293b' }}
                              >
                                {agent.name}
                              </Typography>
                              {agent.role && (
                                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.68rem' }}>
                                  • {agent.role}
                                </Typography>
                              )}
                            </Stack>
                            {agent.task && (
                              <Typography variant="caption" sx={{ color: '#4b5563', fontSize: '0.7rem' }}>
                                {agent.task}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {agent.duration && (
                            <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.68rem' }}>
                              {agent.duration}
                            </Typography>
                          )}
                          {agent.status && (
                            <Chip
                              label={agent.status}
                              size="small"
                              color={agent.status === 'running' ? 'primary' : 'success'}
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                            />
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            )}

            {/* Reasoning Trace Waterfall */}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 1.5, color: '#1e293b' }}>
                ReAct Execution Loop Trace ({traceEvents.length} Turns)
              </Typography>
              {traceEvents.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem', fontStyle: 'italic', py: 2, textAlign: 'center' }}>
                  No reasoning trace or tool execution steps recorded for this step.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {traceEvents.map((event, idx) => (
                    <Box
                      key={event.id || idx}
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor:
                          event.type === 'thought'
                            ? '#bfdbfe'
                            : event.type === 'tool_call'
                              ? '#ddd6fe'
                              : event.type === 'observation' || event.type === 'tool_result'
                                ? '#e2e8f0'
                                : event.type === 'delegation'
                                  ? '#99f6e4'
                                  : event.type === 'approval_required'
                                    ? '#fde68a'
                                    : '#bbf7d0',
                        bgcolor:
                          event.type === 'thought'
                            ? '#f0f9ff'
                            : event.type === 'tool_call'
                              ? '#faf5ff'
                              : event.type === 'observation' || event.type === 'tool_result'
                                ? '#f8fafc'
                                : event.type === 'delegation'
                                  ? '#f0fdfa'
                                  : event.type === 'approval_required'
                                    ? '#fffbeb'
                                    : '#f0fdf4',
                      }}
                    >
                      {/* Step Header */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Chip
                            label={`Step ${event.stepNumber ?? idx + 1}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor:
                                event.type === 'thought'
                                  ? '#dbeafe'
                                  : event.type === 'tool_call'
                                    ? '#ede9fe'
                                    : event.type === 'observation' || event.type === 'tool_result'
                                      ? '#f1f5f9'
                                      : event.type === 'delegation'
                                        ? '#ccfbf1'
                                        : event.type === 'approval_required'
                                          ? '#fef3c7'
                                          : '#dcfce7',
                              color:
                                event.type === 'thought'
                                  ? '#1d4ed8'
                                  : event.type === 'tool_call'
                                    ? '#6d28d9'
                                    : event.type === 'observation' || event.type === 'tool_result'
                                      ? '#475569'
                                      : event.type === 'delegation'
                                        ? '#0f766e'
                                        : event.type === 'approval_required'
                                          ? '#b45309'
                                          : '#15803d',
                            }}
                          />
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem', color: '#334155' }}>
                            {event.type === 'thought'
                              ? 'Model Reasoning'
                              : event.type === 'tool_call'
                                ? `Tool Execution: ${event.toolName ?? 'call'}`
                                : event.type === 'observation' || event.type === 'tool_result'
                                  ? 'Observation / Tool Output'
                                  : event.type === 'delegation'
                                    ? `Delegated to: ${event.subagentDefId ?? 'Worker Agent'}`
                                    : event.type === 'approval_required'
                                      ? 'Human Approval Required'
                                      : 'Final Loop Answer'}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          {event.durationMs !== undefined && (
                            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#64748b' }}>
                              {event.durationMs}ms
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            sx={{ fontSize: '0.68rem', color: '#94a3b8', fontFamily: 'monospace' }}
                          >
                            {event.timestamp}
                          </Typography>
                        </Stack>
                      </Stack>

                      {event.thought && (
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '0.75rem', color: '#1e293b', whiteSpace: 'pre-wrap' }}
                        >
                          {event.thought}
                        </Typography>
                      )}

                      {event.delegatedTask && (
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#0f766e', fontWeight: 500 }}>
                          Task: {event.delegatedTask}
                        </Typography>
                      )}

                      {event.toolInput && (
                        <Box
                          component="pre"
                          sx={{
                            mt: 0.75,
                            mb: 0,
                            p: 1,
                            bgcolor: '#1e293b',
                            color: '#f8fafc',
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            fontFamily: 'monospace',
                            overflow: 'auto',
                            maxHeight: 120,
                          }}
                        >
                          {JSON.stringify(event.toolInput, null, 2)}
                        </Box>
                      )}

                      {event.toolOutput && (
                        <Box
                          component="pre"
                          sx={{
                            mt: 0.75,
                            mb: 0,
                            p: 1,
                            bgcolor: '#f1f5f9',
                            color: '#0f172a',
                            border: '1px solid #cbd5e1',
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            fontFamily: 'monospace',
                            overflow: 'auto',
                            maxHeight: 120,
                          }}
                        >
                          {typeof event.toolOutput === 'string'
                            ? event.toolOutput
                            : JSON.stringify(event.toolOutput, null, 2)}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Stack>
        </Box>
      </CustomTabPanel>

      <CustomTabPanel value={tabIndex} index={2}>
        <Box sx={{ overflowY: 'auto', flexGrow: 1, pr: 0.5 }}>
          <Stack spacing={2}>
            {/* Section 1: Configuration & Lineage Details */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell variant="head" sx={{ width: 180, fontWeight: 700, bgcolor: '#f8fafc' }}>
                      Entity Type
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entityTypeLabel || step.entityType}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                      Entity Definition
                    </TableCell>
                    <TableCell>{defName || step.entityDefName || step.entityDefId}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                      Entity Instance ID
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {step.entityId || '-'}
                        </Typography>
                        {step.entityId && (
                          <Tooltip title={copiedEntityId ? 'Copied!' : 'Copy ID'}>
                            <IconButton size="small" onClick={handleCopyEntityId} sx={{ p: 0.25 }}>
                              {copiedEntityId ? (
                                <CheckIcon sx={{ fontSize: 14, color: '#16a34a' }} />
                              ) : (
                                <ContentCopyIcon sx={{ fontSize: 14, color: '#64748b' }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {instanceUrl && (
                          <Button
                            size="small"
                            variant="outlined"
                            component="a"
                            href={instanceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                            sx={{ height: 20, fontSize: '0.65rem', py: 0, px: 0.75, textTransform: 'none', ml: 1 }}
                          >
                            Open in New Window
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>

                  {/* SOURCE AGENTS DISPLAY */}
                  {(step.entityType === EntityType.userAppInstance || sourceAgents.length > 0) && (
                    <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                        Source Agents ({sourceAgents.length})
                      </TableCell>
                      <TableCell>
                        {sourceAgents.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            No source agents connected
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                            {sourceAgents.map((ag) => (
                              <Chip
                                key={ag.id}
                                icon={<SmartToyIcon sx={{ fontSize: '13px !important' }} />}
                                label={`${ag.name}${ag.entityDefName ? ` (${ag.entityDefName})` : ''}`}
                                size="small"
                                color={ag.inPipeline ? 'primary' : 'default'}
                                variant={ag.inPipeline ? 'filled' : 'outlined'}
                                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                              />
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* CONNECTED DATA SOURCES */}
                  {connectedDataSources.length > 0 && (
                    <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                        Pipeline Data Sources
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                          {connectedDataSources.map((ds) => (
                            <Chip
                              key={ds.entityId}
                              icon={<StorageIcon sx={{ fontSize: '13px !important' }} />}
                              label={ds.entityName || ds.name || 'Data Source'}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* UPSTREAM ENTITIES */}
                  {step.upstreamEntities && step.upstreamEntities.length > 0 && (
                    <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                        Upstream Trigger Entities
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                          {step.upstreamEntities.map((ue, idx) => (
                            <Chip
                              key={`${ue.entityId}-${idx}`}
                              icon={<AltRouteIcon sx={{ fontSize: '13px !important' }} />}
                              label={`${ue.entityName}${ue.inboundEventName ? ` [${ue.inboundEventName}]` : ''}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}

                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                      Inbound Event
                    </TableCell>
                    <TableCell>{step.inboundEventName || 'None / Ingestion'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                      Outbound Event
                    </TableCell>
                    <TableCell>{step.outboundEventName || 'None'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Section 2: Step Last Run Metrics */}
            {step.lastRun && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#334155' }}>
                        Execution Run Details
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head" sx={{ width: 180, fontWeight: 700, bgcolor: '#f8fafc' }}>
                        Last Run ID
                      </TableCell>
                      <TableCell>
                        <code>{step.lastRun.id}</code>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                        Trigger Type
                      </TableCell>
                      <TableCell>{step.lastRun.triggerType || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                        Started
                      </TableCell>
                      <TableCell>
                        {step.lastRun.startTime
                          ? formatDate(new Date(step.lastRun.startTime), 'yyyy-MM-dd HH:mm:ss')
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                        Completed
                      </TableCell>
                      <TableCell>
                        {step.lastRun.endTime
                          ? formatDate(new Date(step.lastRun.endTime), 'yyyy-MM-dd HH:mm:ss')
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                        Duration
                      </TableCell>
                      <TableCell>{formatRunDuration(step.lastRun.startTime, step.lastRun.endTime) || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                        Success Items
                      </TableCell>
                      <TableCell sx={{ color: 'success.main', fontWeight: 600 }}>
                        {step.lastRun.successCount ?? 0}
                      </TableCell>
                    </TableRow>
                    {step.lastRun.failedCount ? (
                      <TableRow>
                        <TableCell variant="head" sx={{ color: 'error.main', fontWeight: 700, bgcolor: '#f8fafc' }}>
                          Failed Items
                        </TableCell>
                        <TableCell sx={{ color: 'error.main', fontWeight: 700 }}>{step.lastRun.failedCount}</TableCell>
                      </TableRow>
                    ) : null}
                    <TableRow>
                      <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                        Total Items
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{step.lastRun.totalItems ?? 0}</TableCell>
                    </TableRow>
                    {step.lastRun.error && (
                      <TableRow>
                        <TableCell variant="head" sx={{ color: 'error.main', fontWeight: 700, bgcolor: '#f8fafc' }}>
                          Error
                        </TableCell>
                        <TableCell sx={{ color: 'error.main' }}>{step.lastRun.error}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Section 3: Entity Usage Stats / Metrics */}
            {loadingStats ? (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading instance metrics...
                </Typography>
              </Stack>
            ) : usageStats ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#334155' }}>
                        Instance Usage & Activity Stats
                      </TableCell>
                    </TableRow>
                    {usageStats.sourceAgents !== undefined && (
                      <TableRow>
                        <TableCell variant="head" sx={{ width: 180, fontWeight: 700, bgcolor: '#f8fafc' }}>
                          Connected Source Agents
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{renderMetricValue(usageStats.sourceAgents)}</TableCell>
                      </TableRow>
                    )}
                    {usageStats.subAgents !== undefined && (
                      <TableRow>
                        <TableCell variant="head" sx={{ width: 180, fontWeight: 700, bgcolor: '#f8fafc' }}>
                          Sub-Agents
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{renderMetricValue(usageStats.subAgents)}</TableCell>
                      </TableRow>
                    )}
                    {usageStats.conversations !== undefined && (
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                          Active Conversations
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{renderMetricValue(usageStats.conversations)}</TableCell>
                      </TableRow>
                    )}
                    {usageStats.interactions !== undefined && (
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                          User Interactions
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{renderMetricValue(usageStats.interactions)}</TableCell>
                      </TableRow>
                    )}
                    {usageStats.dataSources !== undefined && (
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                          Connected Data Sources
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{renderMetricValue(usageStats.dataSources)}</TableCell>
                      </TableRow>
                    )}
                    {usageStats.subscriptions !== undefined && (
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                          Event Subscriptions
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{renderMetricValue(usageStats.subscriptions)}</TableCell>
                      </TableRow>
                    )}
                    {usageStats.subscribers !== undefined && (
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                          Subscribers
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{renderMetricValue(usageStats.subscribers)}</TableCell>
                      </TableRow>
                    )}
                    {usageStats.users !== undefined && (
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>
                          Users
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{renderMetricValue(usageStats.users)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </Stack>
        </Box>
      </CustomTabPanel>
    </Paper>
  );
};

export default StepViewer;
